// components/LeadersSection.tsx

// Add "use client" at the top to enable client-side hooks for animation
"use client"; 

import Image from 'next/image';
import React, { useRef } from 'react';
// Import motion components and hooks from Framer Motion
import { motion, useInView } from 'framer-motion';

// --- DATA FOR THE LEADERS (No changes here) ---
const leadersData = [
  {
    name: 'Mr. Ajay Kumar',
    title: 'Vice Chairman',
    description: 'At Shivalik College, we not only flourish to create an equilibrium that is scholastically enriching, but also additionally focus on the overall development of our students.',
    imageUrl: '/images/CAIRAmAn.jpg',
  },
  {
    name: 'Dr. Prahlad Singh',
    title: 'Director',
    description: 'At Shivalik College, we believe that there is an enormous obligation on our shoulders as educators of the modern world. Since the moment we have set up our establishment in directing students towards the path of a successful career, we have begun to increasingly comprehend the significance and the true meaning of education.',
    imageUrl: '/images/director.jpg',
  },
  {
    name: 'Dr. Surmadhur Pant',
    title: 'Director Admission',
    description: 'As the Dean of Student Welfare and Director Admissions, my primary focus is to ensure that each student receives the support they need to grow not just academically, but holistically.',
    imageUrl: '/images/dsw.jpg',
  },
];

// --- REUSABLE LEADER CARD COMPONENT (Now with animation variants) ---
type LeaderCardProps = {
  name: string;
  title: string;
  description: string;
  imageUrl: string;
};

// Define the animation properties for each card
const cardVariants = {
  hidden: { y: 50, opacity: 0 },
  visible: { 
    y: 0, 
    opacity: 1,
    transition: { duration: 0.6 }
  },
};

const LeaderCard: React.FC<LeaderCardProps> = ({ name, title, description, imageUrl }) => {
  return (
    // Replaced <div> with <motion.div> and added variants
    <motion.div 
      variants={cardVariants}
      className="bg-white rounded-lg shadow-lg p-6 flex flex-col items-center text-center"
    >
      <div className="w-full h-48 relative rounded-md overflow-hidden">
         <Image
            src={imageUrl}
            alt={`Photo of ${name}`}
            layout="fill"
            objectFit="cover"
            className="rounded-md"
         />
      </div>
      <div className="mt-4">
        <h3 className="text-xl font-bold text-gray-800">{name}</h3>
        <p className="text-sm text-gray-500 font-medium mt-1">{title}</p>
        <p className="text-gray-600 mt-3 text-sm">{description}</p>
      </div>
      <button className="mt-5 px-6 py-2 bg-[#8C1B1B] text-white font-semibold rounded-md hover:bg-[#7a1818] transition-colors duration-300">
        Read More
      </button>
    </motion.div>
  );
};

// --- MAIN SECTION COMPONENT (Now with scroll-triggered animation) ---
const LeadersSection = () => {
  // A ref to track the grid container's visibility
  const ref = useRef(null);
  // useInView hook returns true if the element is in the viewport
  const isInView = useInView(ref, { once: true, amount: 0.2 });

  // Define the container variants for staggering the children's animation
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.2, // Each card will animate 0.2s after the previous one
      },
    },
  };

  return (
    <section className="bg-slate-50 py-16 px-4 overflow-hidden">
      <div className="container mx-auto max-w-6xl">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-800">Our Leader's</h2>
          <div className="w-24 h-1 bg-[#8C1B1B] mx-auto mt-2"></div>
          <p className="text-gray-500 mt-4">
            Experienced educators guiding you to success.
          </p>
        </div>

        {/* - Grid container is now a <motion.div>
          - It uses the ref to detect when it's on screen.
          - 'animate' is dynamically set to "visible" when isInView is true.
          - 'variants' with 'staggerChildren' orchestrates the card animations.
          - The grid classes ensure it is perfectly RESPONSIVE.
        */}
        <motion.div
          ref={ref}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
        >
          {leadersData.map((leader) => (
            <LeaderCard
              key={leader.name}
              name={leader.name}
              title={leader.title}
              description={leader.description}
              imageUrl={leader.imageUrl}
            />
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default LeadersSection;